import {
    Complaint,
    ComplaintAppointment,
    ComplaintHandlingLevel,
    ComplaintUserType,
    DocumentStatus,
    Workflow,
} from '@icc/b2b/shared';

/**
 * Konfiguracja przycisków akcji reklamacji.
 *
 * Definiuje dla każdego przycisku:
 *  - `ursLevel`    — poziom URS (userRolesStatuses) w którym przycisk jest sprawdzany
 *  - `ursStatusField` — pole dokumentu zawierające statusId używane do sprawdzenia przycisku
 *                       (null = własny status aktualnego poziomu)
 *  - `forLevel`    — poziomy, dla których przycisk ma w ogóle sens
 *
 * UWAGA: To jest jedyne źródło prawdy dla tej konfiguracji.
 * Używane zarówno przez complaint.component.ts (przez isButtonAvailableForLevel)
 * jak i przez ComplaintDiagnosticService.
 * Przy dodawaniu nowego przycisku — edytuj TYLKO to miejsce.
 */
export interface ButtonLevelConfig {
    ursLevel: ComplaintHandlingLevel | null; // null = własny handlingLevel
    ursStatusField: 'distributorStatusId' | 'manufacturerStatusId' | null; // null = własny status
    forLevel: ComplaintUserType[];
}

export const COMPLAINT_BUTTON_CONFIG: Record<string, ButtonLevelConfig> = {
    'appeal':                         { ursLevel: null, ursStatusField: null,                forLevel: ['authorizedRetailer', 'distributor', 'manufacturer'] },
    'attach_aps':                     { ursLevel: null, ursStatusField: null,                forLevel: ['authorizedRetailer', 'distributor', 'manufacturer'] },
    'detach_aps':                     { ursLevel: null, ursStatusField: null,                forLevel: ['authorizedRetailer', 'distributor', 'manufacturer'] },
    'assign_to_distributor':          { ursLevel: null, ursStatusField: null,                forLevel: ['authorizedRetailer', 'distributor', 'manufacturer'] },
    'assign_to_manufacturer':         { ursLevel: null, ursStatusField: null,                forLevel: ['authorizedRetailer', 'distributor', 'manufacturer'] },
    'create_appointment':             { ursLevel: null, ursStatusField: null,                forLevel: ['authorizedRetailer', 'distributor', 'manufacturer'] },
    'appeal_to_distributor':          { ursLevel: 'distributor',   ursStatusField: 'distributorStatusId',  forLevel: ['authorizedRetailer'] },
    'received_from_distributor':      { ursLevel: 'distributor',   ursStatusField: 'distributorStatusId',  forLevel: ['authorizedRetailer'] },
    'parts_sent_aps':                 { ursLevel: 'distributor',   ursStatusField: 'distributorStatusId',  forLevel: ['authorizedRetailer'] },
    'return_resignation_aps':         { ursLevel: 'distributor',   ursStatusField: 'distributorStatusId',  forLevel: ['authorizedRetailer'] },
    'appeal_to_manufacturer':         { ursLevel: 'manufacturer',  ursStatusField: 'manufacturerStatusId', forLevel: ['distributor'] },
    'received_from_manufacturer':     { ursLevel: 'manufacturer',  ursStatusField: 'manufacturerStatusId', forLevel: ['distributor'] },
    'parts_sent_distributor':         { ursLevel: 'distributor',   ursStatusField: 'distributorStatusId',  forLevel: ['distributor', 'manufacturer'] },
    'return_resignation_distributor': { ursLevel: 'distributor',   ursStatusField: 'distributorStatusId',  forLevel: ['distributor', 'manufacturer'] },
};

/**
 * Sprawdza dostępność przycisku akcji dla danego poziomu użytkownika.
 * Czysta funkcja — używana przez ComplaintService.isButtonAvailable() i ComplaintDiagnosticService.
 *
 * @param buttonName  - nazwa przycisku (klucz z COMPLAINT_BUTTON_CONFIG)
 * @param workflow    - dopasowany workflow
 * @param document    - dokument reklamacji
 * @param handlingLevel - poziom obsługi aktualnego użytkownika
 * @param userType    - typ użytkownika (authorizedRetailer | distributor | manufacturer)
 * @param currentStatusId - aktualny statusId użytkownika
 * @param userRoleIds - role użytkownika ([] = wildcard sprawdzony przez Number(rid)===0)
 */
export function isButtonAvailableForLevel(
    buttonName: string,
    workflow: Workflow,
    document: Complaint,
    handlingLevel: ComplaintHandlingLevel,
    userType: ComplaintUserType,
    currentStatusId: number,
    userRoleIds: number[]
): boolean {
    const cfg = COMPLAINT_BUTTON_CONFIG[buttonName];
    if (!cfg) {
        return false;
    }
    if (!cfg.forLevel.includes(userType)) {
        return false;
    }

    const ursLevel = cfg.ursLevel ?? handlingLevel;
    const ursStatusId = cfg.ursStatusField ? (+document[cfg.ursStatusField] || -1) : currentStatusId;

    return workflow.userRolesStatuses.some(
        (el) =>
            el.level === ursLevel
            && el.userRolesIds.some(
                (rid) => Number(rid) === 0 || userRoleIds.includes(Number(rid))
            )
            && (el.buttons?.[ursStatusId] as any)?.[buttonName]?.available
    );
}

/**
 * Zwraca ID statusu dokumentu dla danego typu użytkownika.
 */
export function getDocumentStatusForUser(
    document: Complaint,
    userType: ComplaintUserType
): number {
    return +document[
        `${userType}StatusId` as
            | 'authorizedRetailerStatusId'
            | 'distributorStatusId'
            | 'manufacturerStatusId'
    ]!;
}

/**
 * Zwraca poziom obsługi reklamacji dla danego dokumentu i typu użytkownika.
 * Identyczna logika jak w complaint.service.ts i server-complaint.service.ts.
 */
export function getComplaintHandlingLevel(
    document: Complaint,
    userType?: ComplaintUserType
): ComplaintHandlingLevel {
    if (
        document?.clientName?.length > 0
        && document.authorizedRetailerCompanyId == null
        && document.distributorCompanyId != null
        && userType === 'distributor'
    ) {
        return 'clientDistributor';
    }
    return userType!;
}

/**
 * Sprawdza czy wizyta serwisowa spełnia warunek protokołu dla przejścia statusu.
 * Identyczna logika jak checkAppointments w complaint.service.ts.
 */
export function checkAppointments(
    appointments: ComplaintAppointment[],
    status: { protocol?: boolean }
): boolean {
    return (
        !status.protocol
        || appointments.some(
            (el) =>
                !el.isDeleted
                && el.status === 'completed'
                && el.protocolDate
                && el.servicemanSignatureImage
        )
    );
}

export interface GetAvailableStatusesInput {
    document: Complaint;
    positions: { configuration?: any; complaintProductTypeId?: number }[];
    statuses: DocumentStatus[];
    workflows: Workflow[];
    positionsWorkflowsGroups: Map<number, any[]>;
    appointments: ComplaintAppointment[];
    userType: ComplaintUserType;
    userRoles: number[];
    allPositionsHaveConfiguration: boolean;
}

/**
 * Czysta funkcja zwracająca dostępne statusy dla danego poziomu obsługi reklamacji.
 * Dokładnie ta sama logika co getAvailableStatuses() w ComplaintService na froncie.
 * Dzięki temu backend diagnostyczny używa tego samego algorytmu filtrowania statusów.
 */
export function getAvailableStatusesForLevel(
    input: GetAvailableStatusesInput
): DocumentStatus[] {
    const {
        document,
        statuses,
        positionsWorkflowsGroups,
        appointments,
        userType,
        userRoles,
        allPositionsHaveConfiguration,
    } = input;

    const rawStatusId = getDocumentStatusForUser(document, userType);
    const documentStatusId = Number.isFinite(rawStatusId) ? rawStatusId : -1;

    if (positionsWorkflowsGroups.size > 1) {
        return statuses.filter((status) => status.id === +documentStatusId);
    }

    const positionsWorkflow = input.workflows.find(
        (el) => +el.id === +(positionsWorkflowsGroups.keys().next().value ?? 0)
    );
    const complaintHandlingLevel: ComplaintHandlingLevel = getComplaintHandlingLevel(
        document,
        userType
    );

    return statuses.filter(
        (status) =>
            status.id === +documentStatusId
            || (
                +status.documentTypeId === +document.documentTypeId
                && status.complaintHandlingLevel === complaintHandlingLevel
                && positionsWorkflow?.userRolesStatuses.some(
                    (el) =>
                        el.level === complaintHandlingLevel
                        && el.userRolesIds.some(
                            (roleId) =>
                                Number(roleId) === 0
                                || userRoles.includes(Number(roleId))
                        )
                        && el.statuses[+documentStatusId]?.[status.id]?.available
                        && checkAppointments(
                            appointments,
                            el.statuses[+documentStatusId]?.[status.id]
                        )
                        && (
                            allPositionsHaveConfiguration
                            || !el.statuses.lockedWhenLackConfiguration?.[status.id]
                                ?.available
                        )
                )
            )
    );
}
